import type { TourState } from '../../shared/types.js';
import { plural, t } from '../i18n/i18n.js';
import { clear, el, hex, ordinal } from './dom.js';

/**
 * The end of a cup.
 *
 * A results table is a fine way to end one race. It is a terrible way to end five, because
 * by then the thing that happened is not a time — it is that somebody *won*, after an hour,
 * and the screen has one job: to say so loudly enough that the room reacts.
 *
 * So: the winner's name at the size of a headline, three podium blocks that rise out of the
 * floor in reverse order so the top step lands last, and confetti. The staging is the
 * content. Everything sober — the full standings, who scored what where — sits underneath
 * it, for the two minutes afterwards when people want to argue about race three.
 */

/** Milliseconds. The reveal is a sequence, and these are its beats. */
const BEAT_BARS = 240;
const BEAT_BETWEEN_BARS = 420;
const BEAT_WINNER = BEAT_BARS + BEAT_BETWEEN_BARS * 2 + 520;

/**
 * Confetti, in pieces and in seconds of falling.
 *
 * 220 is where it stops reading as "some confetti" and starts reading as a celebration on a
 * 1280-wide window; past about 400 it costs a visible frame on an integrated GPU at exactly
 * the moment the rest of the scene is still rendering behind it.
 */
const CONFETTI_PIECES = 220;
const CONFETTI_SECONDS = 7;

const PLACE_COLORS = ['#ffd34d', '#cfd8e3', '#d9924b'];
/** Relative heights of the three steps. First is the whole column; the others step down. */
const PLACE_HEIGHTS = [1, 0.68, 0.5];

export interface PodiumActions {
  /** Run the same cup again from zero. Host only. */
  onAgain(): void;
  /** Drop the tour and go back to the lobby. Host only. */
  onLobby(): void;
  onLeave(): void;
}

export class Podium {
  readonly root: HTMLElement;
  private readonly confettiCanvas: HTMLCanvasElement;
  private readonly stage: HTMLElement;
  private readonly winnerName: HTMLElement;
  private readonly winnerSub: HTMLElement;
  private readonly eyebrow: HTMLElement;
  private readonly tableHead: HTMLElement;
  private readonly tableBody: HTMLElement;
  private readonly actions: HTMLElement;

  private confetti: Confetti | null = null;
  private timers: number[] = [];

  constructor() {
    this.confettiCanvas = el('canvas', { class: 'podium-confetti' });
    this.eyebrow = el('div', { class: 'podium-eyebrow' });
    this.winnerName = el('div', { class: 'podium-winner-name' });
    this.winnerSub = el('div', { class: 'podium-winner-sub' });
    this.stage = el('div', { class: 'podium-stage' });
    this.tableHead = el('thead', {});
    this.tableBody = el('tbody', {});
    this.actions = el('div', { class: 'podium-actions' });

    this.root = el(
      'div',
      { class: 'podium is-hidden', id: 'podium' },
      this.confettiCanvas,
      el(
        'div',
        // Inside `.screen-game`, so in the dark scheme with the rest of the race — a card of
        // its own on a scrim, rather than the HUD's glass, is what makes it the end of a cup.
        { class: 'podium-inner' },
        this.eyebrow,
        el(
          'div',
          { class: 'podium-winner' },
          el('div', { class: 'podium-crown' }, '🏆'),
          this.winnerName,
          this.winnerSub,
        ),
        this.stage,
        el('table', { class: 'podium-table' }, this.tableHead, this.tableBody),
        this.actions,
      ),
    );
  }

  show(tour: TourState, localId: string, isHost: boolean, actions: PodiumActions): void {
    this.clearTimers();
    const standings = tour.standings;
    const winner = standings[0];

    this.eyebrow.textContent = plural(tour.stops.length, '{name} · {n} race', '{name} · {n} races', {
      name: tour.name,
    });

    if (!winner) {
      // Everybody left before a single race scored. There is nothing to celebrate, and
      // pretending otherwise with an empty podium is worse than saying so.
      this.winnerName.textContent = t('No result');
      this.winnerSub.textContent = t('Nobody finished the cup.');
    } else {
      this.winnerName.textContent = winner.name;
      this.winnerName.style.color = hex(winner.color);
      const runnerUp = standings[1];
      const margin = runnerUp ? winner.points - runnerUp.points : 0;
      this.winnerSub.textContent =
        winner.id === localId
          ? plural(winner.points, 'You win the cup with {n} points', 'You win the cup with {n} points')
          : plural(winner.points, 'wins the cup with {n} points', 'wins the cup with {n} points');
      if (runnerUp) {
        this.winnerSub.textContent += margin === 0 ? t(' — on countback') : plural(margin, ' — by {n}', ' — by {n}');
      }
    }

    this.buildStage(standings.slice(0, 3), localId);
    this.buildTable(tour, localId);
    this.buildActions(isHost, actions);

    this.root.classList.remove('is-hidden');
    // Off, then on across a frame, so the transitions actually run. Setting the class in
    // the same tick as the nodes are inserted means the browser never sees the "before".
    this.root.classList.remove('is-live');
    requestAnimationFrame(() => this.root.classList.add('is-live'));

    this.timers.push(
      window.setTimeout(() => {
        this.confetti?.stop();
        this.confetti = new Confetti(this.confettiCanvas);
        this.confetti.burst(CONFETTI_PIECES, CONFETTI_SECONDS);
      }, BEAT_WINNER),
    );
  }

  /**
   * The three steps, laid out 2nd–1st–3rd so the tallest is in the middle, but revealed
   * 3rd–2nd–1st so the winner's block is the last thing to land.
   */
  private buildStage(top: TourState['standings'], localId: string): void {
    clear(this.stage);
    const order = [1, 0, 2];
    for (const place of order) {
      const row = top[place];
      const column = el('div', {
        class: `podium-column podium-place-${place + 1}${row?.id === localId ? ' is-self' : ''}`,
        style: {
          // Both are read by the CSS: one sizes the block, the other delays its rise.
          ['--podium-height' as string]: String(PLACE_HEIGHTS[place]),
          ['--podium-delay' as string]: `${BEAT_BARS + (2 - place) * BEAT_BETWEEN_BARS}ms`,
          ['--podium-accent' as string]: PLACE_COLORS[place],
        },
      });
      if (!row) {
        // Fewer than three drivers. An empty step is drawn rather than skipped, because a
        // podium with a gap where second place should be says something true.
        column.classList.add('is-empty');
        column.append(el('div', { class: 'podium-block' }, el('span', { class: 'podium-place-number' }, String(place + 1))));
        this.stage.append(column);
        continue;
      }
      column.append(
        el(
          'div',
          { class: 'podium-driver' },
          el('span', { class: 'podium-swatch', style: { background: hex(row.color) } }),
          el('span', { class: 'podium-driver-name' }, row.name),
          row.ai ? el('span', { class: 'tag' }, 'AI') : null,
        ),
        el('div', { class: 'podium-driver-points' }, t('{n} pts', { n: row.points })),
        el(
          'div',
          { class: 'podium-block' },
          el('span', { class: 'podium-place-number' }, ordinal(place + 1)),
        ),
      );
      this.stage.append(column);
    }
  }

  private buildTable(tour: TourState, localId: string): void {
    clear(this.tableHead);
    clear(this.tableBody);
    this.tableHead.append(
      el(
        'tr',
        {},
        el('th', {}, '#'),
        el('th', {}, t('Driver')),
        ...tour.stops.map((stop, index) =>
          el('th', { class: 'podium-stop', title: stop.title }, String(index + 1)),
        ),
        el('th', {}, t('Pts')),
      ),
    );
    for (const row of tour.standings) {
      this.tableBody.append(
        el(
          'tr',
          { class: row.id === localId ? 'is-self' : '' },
          el('td', { class: 'tabular' }, String(row.rank)),
          el(
            'td',
            {},
            el('span', { class: 'swatch', style: { background: hex(row.color) } }),
            el('span', {}, row.name),
            row.ai ? el('span', { class: 'tag' }, 'AI') : null,
          ),
          ...tour.stops.map((_, index) => {
            const place = row.positions[index] ?? null;
            return el(
              'td',
              { class: `tabular${place === 1 ? ' is-win' : ''}` },
              place === null ? '–' : String(place),
            );
          }),
          el('td', { class: 'tabular podium-points' }, String(row.points)),
        ),
      );
    }
  }

  private buildActions(isHost: boolean, actions: PodiumActions): void {
    clear(this.actions);
    if (isHost) {
      const again = el('button', { class: 'button button-primary', type: 'button', id: 'tour-again' }, t('Race the cup again'));
      again.addEventListener('click', () => actions.onAgain());
      const lobby = el('button', { class: 'button', type: 'button', id: 'tour-to-lobby' }, t('Back to lobby'));
      lobby.addEventListener('click', () => actions.onLobby());
      this.actions.append(again, lobby);
    } else {
      this.actions.append(el('p', { class: 'results-wait' }, t('Waiting for the host…')));
    }
    const leave = el('button', { class: 'button button-ghost', type: 'button' }, t('Leave race'));
    leave.addEventListener('click', () => actions.onLeave());
    this.actions.append(leave);
  }

  hide(): void {
    this.root.classList.add('is-hidden');
    this.root.classList.remove('is-live');
    this.clearTimers();
    this.confetti?.stop();
    this.confetti = null;
  }

  get visible(): boolean {
    return !this.root.classList.contains('is-hidden');
  }

  private clearTimers(): void {
    for (const timer of this.timers) window.clearTimeout(timer);
    this.timers = [];
  }

  dispose(): void {
    this.hide();
  }
}

// ---------------------------------------------------------------------------
// Confetti
// ---------------------------------------------------------------------------

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Half-width and half-height, in pixels. */
  w: number;
  h: number;
  spin: number;
  angle: number;
  color: string;
}

const CONFETTI_COLORS = [
  '#ffd34d',
  '#ff6bb5',
  '#4cc38a',
  '#39a0ff',
  '#a06cff',
  '#e8503a',
  '#ffffff',
];

/**
 * A canvas of falling paper.
 *
 * Rectangles rather than sprites, and the spin is faked by scaling the width by
 * `cos(angle)` instead of rotating the context — a rotation per piece per frame is 220
 * matrix pushes and pops, and the difference on screen at this size is nothing. What the
 * `cos` does give, for free, is the flash as a piece turns edge-on, which is most of what
 * makes falling paper look like paper.
 */
class Confetti {
  private pieces: Piece[] = [];
  private frame = 0;
  private last = 0;
  private remaining = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {}

  burst(count: number, seconds: number): void {
    const dpr = Math.min(window.devicePixelRatio ?? 1, 2);
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);

    this.pieces = Array.from({ length: count }, () => ({
      // Launched from just above the top edge across the full width, with a spread of
      // start times faked by starting them at different heights.
      x: Math.random() * width,
      y: -Math.random() * height * 1.4,
      vx: (Math.random() - 0.5) * 90,
      vy: 120 + Math.random() * 220,
      w: 3 + Math.random() * 5,
      h: 5 + Math.random() * 8,
      spin: (Math.random() - 0.5) * 9,
      angle: Math.random() * Math.PI * 2,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    }));

    this.remaining = seconds;
    this.last = performance.now();
    this.canvas.style.opacity = '1';
    const context = this.canvas.getContext('2d');
    if (!context) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);

    const step = (now: number) => {
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      this.remaining -= dt;

      context.clearRect(0, 0, width, height);
      for (const piece of this.pieces) {
        piece.x += piece.vx * dt;
        piece.y += piece.vy * dt;
        piece.angle += piece.spin * dt;
        // A little sideways drift, so the fall is not 220 parallel lines.
        piece.vx += Math.sin(piece.angle) * 12 * dt;
        if (piece.y > height + 20) piece.y = -20;
        const w = piece.w * Math.abs(Math.cos(piece.angle));
        context.fillStyle = piece.color;
        context.fillRect(piece.x - w / 2, piece.y - piece.h / 2, Math.max(0.6, w), piece.h);
      }

      // Fade the last second rather than cutting: paper does not vanish.
      if (this.remaining < 1) this.canvas.style.opacity = String(Math.max(0, this.remaining));
      if (this.remaining <= 0) {
        context.clearRect(0, 0, width, height);
        this.frame = 0;
        return;
      }
      this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }

  stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.pieces = [];
    const context = this.canvas.getContext('2d');
    context?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
